import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, message, Modal } from 'antd';
import { saveBillingClaimScope } from 'api/insuranceBilling';
import SectionHeader from 'pages/InsuranceBillingOnboarding/components/SectionHeader';
import EhrBillingCheckList from 'pages/InsuranceBillingOnboarding/components/EhrBillingCheckList';
import HiddenFormField from 'pages/InsuranceBillingOnboarding/components/HiddenFormField';
import { ClaimScopeField, ClaimStartFields } from 'pages/InsuranceBillingOnboarding/steps/StepEhrScope';
import { onboardingDtoToFormValues, stepValuesToDto } from 'pages/InsuranceBillingOnboarding/billingReviewData';
import type { InsuranceBillingOnboardingVo } from 'types/insuranceBilling';
import type { TEhrConnectionItem } from 'types/ehrConnection';
import s from './s.module.less';

// 「Bill for more patients」弹窗（原型 openScopeModal）。
// 装的就是 onboarding Step 6 的那两个范围问题，只是搬进了弹窗 —— 起步时把范围定窄
// （只计费打标签的患者、或只从今天起算）的 provider，看到第一批 claim 落地后想放宽，
// 不该还要绕回 Settings 走一遍。字段直接复用 Step 6 导出的那两个组件，保证两处口径一致。
type Props = {
    open: boolean;
    // dashboard 已经拉过的 onboarding 聚合数据，弹窗拿它做初始值，不重复请求
    vo: InsuranceBillingOnboardingVo | null | undefined;
    connections: TEhrConnectionItem[];
    onClose: () => void;
    // 保存成功后让外层重拉 claimSettings（页面上的范围文案依赖它）
    onSaved: () => void;
};

const BillMorePatientsModal = ({ open, vo, connections, onClose, onSaved }: Props) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    // 「有没有改过」：没改动时 Save 禁用，避免一次空提交把服务端的值原样写回去
    const [dirty, setDirty] = useState(false);

    const billingEhrs: string[] = Form.useWatch(['ehr', 'billingEhrs'], form) || [];

    // 已连上的 + 自建（isCustom）都能计费，与 Step 6 的口径一致
    const checkItems = useMemo(
        () =>
            connections
                .filter((c) => c.connectionStatus === 'connected' || c.isCustom)
                .map((c) => ({ name: c.ehrSystem, pending: !!c.isCustom })),
        [connections],
    );
    const multiEhr = checkItems.length > 1;
    const claimScope = Form.useWatch(['ehr', 'claimScope'], form);

    // billingEhrs 里可能有没列出来的名字（正在连接 / 已断开的 EHR 仍留在计费范围里，由后端裁决），
    // 所以「开着几个」只能按列出来的这些算，不能直接看 billingEhrs.length
    const listedNames = useMemo(() => checkItems.map((c) => c.name), [checkItems]);
    const countOn = (list: string[]) => list.filter((n) => listedNames.includes(n)).length;

    // 每次打开都用服务端的最新值重置：弹窗取消掉的改动不该留到下一次
    useEffect(() => {
        if (!open) return;
        form.setFieldsValue(onboardingDtoToFormValues(vo));
        setDirty(false);
    }, [open, vo, form]);

    // 全部 EHR 都关掉就什么都不计费了，正好与这个弹窗的目的相反，所以不允许保存
    const noEhrOn = checkItems.length > 0 && countOn(billingEhrs) === 0;

    const handleSave = async () => {
        setSaving(true);
        try {
            const dto = stepValuesToDto('claim-scope', form.getFieldsValue(true));
            const res: any = await saveBillingClaimScope(dto);
            if (res?.error) return;
            message.success('Billing scope updated');
            onSaved();
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={640}
            destroyOnClose
            title={
                <div>
                    <div className={s.scopeModalTitle}>Bill for more patients</div>
                    <div className={s.scopeModalSub}>
                        Widen what Klarity bills for. Changes apply from the next sync.
                    </div>
                </div>
            }
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    className={s.blueBtn}
                    loading={saving}
                    disabled={!dirty || noEhrOn}
                    onClick={handleSave}
                >
                    Save changes
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" onValuesChange={() => setDirty(true)}>
                {/* billingEhrs 只存值，靠 HiddenFormField 注册字段供 useWatch 响应 */}
                <Form.Item name={['ehr', 'billingEhrs']} noStyle>
                    <HiddenFormField />
                </Form.Item>
                {checkItems.length > 0 && (
                    <div className={s.scopeSection}>
                        {/* 只有一个 EHR 时没什么可选的，但仍然列出来，让人看清下面的范围作用在谁身上 */}
                        <SectionHeader
                            title={multiEhr ? 'Which EHRs should Klarity bill from?' : 'Klarity bills from'}
                        />
                        <EhrBillingCheckList
                            items={checkItems}
                            value={billingEhrs}
                            onChange={(next) => {
                                // 唯一的 EHR 不能取消：关掉它等于什么都不计费，正好与这个弹窗的目的相反。
                                // 注意不能直接 return —— checkbox 是受控的，浏览器已经原生把它勾掉了，
                                // 若不触发一次重渲染，React 不会把它拨回来，看上去就是「取消成功了」。
                                // 所以原值换个新引用写回去，逼 useWatch 走一轮。
                                if (!multiEhr && countOn(next) === 0) {
                                    form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs]);
                                    return;
                                }
                                form.setFieldValue(['ehr', 'billingEhrs'], next);
                                setDirty(true);
                            }}
                        />
                        {!multiEhr && (
                            <p className={s.scopeHint}>
                                Klarity bills from this EHR. Connect another in Settings to bill from more than one.
                            </p>
                        )}
                        {noEhrOn && <p className={s.scopeWarn}>Klarity bills nothing while every EHR is off.</p>}
                    </div>
                )}

                <div className={s.scopeSection}>
                    <SectionHeader title="Which patients should Klarity bill for?" />
                    <ClaimScopeField />
                    {/* 标签存在 EHR 里，连了多个就得每个都打一遍，否则那些 EHR 会整个被跳过 */}
                    {multiEhr && claimScope === 'tagged' && (
                        <p className={s.scopeHint}>
                            Applies to each EHR above: tag the patients you want billed in every one you bill from.
                        </p>
                    )}
                </div>

                <div className={s.scopeSection}>
                    <SectionHeader title="When should Klarity start processing claims?" />
                    <ClaimStartFields />
                </div>
            </Form>
        </Modal>
    );
};

export default BillMorePatientsModal;
